'use client'

import Link from 'next/link'
import { ArrowUpRight } from 'lucide-react'
import useSWR from 'swr'
import { entryHref } from '@/lib/cms/content'
import type { CmsEntryDto } from '@/lib/cms/types'

const fetcher = (url: string) => fetch(url).then(response => {
  if (!response.ok) throw new Error('Content unavailable')
  return response.json() as Promise<{ learn: CmsEntryDto[]; blog: CmsEntryDto[] }>
})

function Story({ entry }: { entry: CmsEntryDto }) {
  const href = entryHref(entry)
  const content = (
    <>
      <div className="aspect-[16/10] overflow-hidden border-2 ninxl-theme-border-soft bg-black/5">
        {entry.thumbnailUrl ? <img src={entry.thumbnailUrl} alt="" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" loading="lazy" /> : null}
      </div>
      <div className="mt-3 flex items-start justify-between gap-3">
        <div><div className="ninxl-8bit-label ninxl-theme-text-accent mb-1">{entry.kind === 'learn' ? 'LEARN' : 'BLOG'} · {entry.category?.name || 'NIN'}</div><h3 className="ninxl-theme-text text-sm font-semibold leading-snug">{entry.title}</h3></div>
        <ArrowUpRight className="mt-1 size-4 shrink-0 ninxl-theme-text" aria-hidden="true" />
      </div>
    </>
  )
  return entry.destination === 'external' ? <a href={href} target="_blank" rel="noopener noreferrer" className="group block">{content}</a> : <Link href={href} className="group block">{content}</Link>
}

export function CmsLandingHighlights() {
  const { data } = useSWR('/api/cms/featured', fetcher, { revalidateOnFocus: false, shouldRetryOnError: false })
  const entries = data ? [...data.learn.slice(0, 2), ...data.blog.slice(0, 2)] : []
  if (!entries.length) return null
  return (
    <section className="relative z-20 mx-auto max-w-6xl px-6 py-16" aria-labelledby="nin-editorial-heading">
      <div className="mb-8 flex items-end justify-between gap-6">
        <div><div className="ninxl-8bit-label ninxl-theme-text-accent mb-2">FIELD NOTES</div><h2 id="nin-editorial-heading" className="ninxl-8bit-heading ninxl-theme-text text-base md:text-lg">Learn what NIN is building toward.</h2></div>
        <div className="hidden gap-4 sm:flex"><Link href="/cms/learn" className="ninxl-8bit-label ninxl-theme-link">All lessons</Link><Link href="/cms/blogs" className="ninxl-8bit-label ninxl-theme-link">All stories</Link></div>
      </div>
      <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">{entries.map(entry => <Story key={entry.id} entry={entry} />)}</div>
    </section>
  )
}
